import { useState } from "react";
import MemberListPanel from "./components/member-list-panel";
import MemberDetailPanel from "./components/member-detail-panel";
import MissionDetailPanel from "./components/mission-detail-panel";
import BingoDetailPanel from "./components/bingo-detail-panel";
import RevertProgressConfirmPopup from "./components/revert-progress-confirm-popup";
import { useMemberProgressPage } from "./hooks/use-member-progress-page";
import * as S from "./styles";

const parseAmount = (value: string, fallback = 1) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const EventMemberProgress = () => {
  const {
    memberSearch,
    setMemberSearch,
    memberPage,
    setMemberPage,
    memberTotal,
    memberLimit,
    members,
    fetchMembers,
    selectedMember,
    selectMember,
    inProgressMissions,
    inProgressMissionTotal,
    inProgressMissionFilter,
    setInProgressMissionFilter,
    listLimit,
    completedMissions,
    completedMissionTotal,
    completedMissionFilter,
    setCompletedMissionFilter,
    inProgressBingos,
    inProgressBingoTotal,
    inProgressBingoFilter,
    setInProgressBingoFilter,
    completedBingos,
    completedBingoTotal,
    completedBingoFilter,
    setCompletedBingoFilter,
    selectedMissionRowId,
    selectedMissionId,
    selectMission,
    missionDetail,
    missionDetailLoading,
    missionProgressInput,
    setMissionProgressInput,
    completeMission,
    cancelMissionComplete,
    saveMissionProgress,
    selectedBingoId,
    selectBingo,
    bingoDetail,
    bingoDetailLoading,
    selectedCell,
    selectCell,
    cellProgressInput,
    setCellProgressInput,
    completeBingoCell,
    cancelBingoCell,
    saveCellProgress,
  } = useMemberProgressPage();

  const [pendingRevertAction, setPendingRevertAction] = useState<
    ((memo: string) => void) | null
  >(null);

  const requestRevertConfirm = (action: (memo: string) => void) => {
    setPendingRevertAction(() => action);
  };

  const memberTotalPages = Math.max(1, Math.ceil(memberTotal / memberLimit));

  const handleMissionSave = () => {
    if (!missionDetail) return;
    const target = missionDetail.targetCount ?? 0;
    const next = Math.max(
      0,
      Math.min(
        target,
        parseAmount(missionProgressInput, missionDetail.currentCount),
      ),
    );
    saveMissionProgress(next);
  };

  const handleCellSave = () => {
    if (!selectedCell) return;
    const target = selectedCell.targetCount ?? 0;
    const next = Math.max(
      0,
      Math.min(
        target,
        parseAmount(cellProgressInput, selectedCell.currentCount),
      ),
    );
    saveCellProgress(selectedCell.id, next);
  };

  return (
    <S.PageWrapper>
      <S.Title>유저 진행 현황</S.Title>

      <S.TabRow>
        <S.TabItem>유저별 관리</S.TabItem>
      </S.TabRow>

      <S.ContentRow>
        <S.ContentColumn>
          <MemberListPanel
            members={members}
            selectedMemberId={selectedMember?.memberId}
            search={memberSearch}
            onSearchChange={setMemberSearch}
            onSearch={() => {
              setMemberPage(1);
              fetchMembers();
            }}
            currentPage={memberPage}
            totalPages={memberTotalPages}
            onPageChange={setMemberPage}
            onSelect={selectMember}
          />
        </S.ContentColumn>

        <S.ContentColumn>
          {selectedMember && (
            <MemberDetailPanel
              member={selectedMember}
              inProgressMissions={inProgressMissions}
              inProgressMissionTotal={inProgressMissionTotal}
              inProgressMissionFilter={inProgressMissionFilter}
              onInProgressMissionFilterChange={setInProgressMissionFilter}
              completedMissions={completedMissions}
              completedMissionTotal={completedMissionTotal}
              completedMissionFilter={completedMissionFilter}
              onCompletedMissionFilterChange={setCompletedMissionFilter}
              inProgressBingos={inProgressBingos}
              inProgressBingoTotal={inProgressBingoTotal}
              inProgressBingoFilter={inProgressBingoFilter}
              onInProgressBingoFilterChange={setInProgressBingoFilter}
              completedBingos={completedBingos}
              completedBingoTotal={completedBingoTotal}
              completedBingoFilter={completedBingoFilter}
              onCompletedBingoFilterChange={setCompletedBingoFilter}
              listLimit={listLimit}
              selectedMissionId={selectedMissionRowId}
              selectedBingoId={selectedBingoId}
              onSelectMission={selectMission}
              onSelectBingo={selectBingo}
            />
          )}
        </S.ContentColumn>

        <S.ContentColumn>
          {selectedMissionId && (
            <MissionDetailPanel
              detail={missionDetail}
              loading={missionDetailLoading}
              progressInput={missionProgressInput}
              onProgressInputChange={setMissionProgressInput}
              onComplete={completeMission}
              onCancelComplete={() =>
                requestRevertConfirm((memo) => cancelMissionComplete(memo))
              }
              onSaveProgress={handleMissionSave}
            />
          )}
          {selectedBingoId && (
            <BingoDetailPanel
              detail={bingoDetail}
              loading={bingoDetailLoading}
              selectedCell={selectedCell}
              progressInput={cellProgressInput}
              onSelectCell={selectCell}
              onProgressInputChange={setCellProgressInput}
              onCompleteCell={() =>
                selectedCell && completeBingoCell(selectedCell.id)
              }
              onCancelCell={() =>
                selectedCell &&
                requestRevertConfirm((memo) =>
                  cancelBingoCell(selectedCell.id, memo),
                )
              }
              onSaveProgress={handleCellSave}
            />
          )}
        </S.ContentColumn>
      </S.ContentRow>

      {pendingRevertAction && (
        <RevertProgressConfirmPopup
          onClose={() => setPendingRevertAction(null)}
          onConfirm={pendingRevertAction}
        />
      )}
    </S.PageWrapper>
  );
};

export default EventMemberProgress;
